import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { IBaseFacade } from '@shared/base-classes/base.facade';
import { PatientState, PatientStore } from './state/patient-store';
import { PatientQuery } from './state/patient-query';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { PatientModel } from '@shared/models/rx-models/interfaces/patient-model';
import { patientSectionModeEnum } from './models/patient-section-visibility.enum';
import { PatientDisplayMode } from './models/patient-display-mode.enum';
import { PatientDisplayModeService } from './services/patient-display-mode.service';
import { PopupService } from '@shared/services/popup.service';
import { PatientAppIframeCommunicationService } from './services/patient-app-iframe-communication.service';
import { RxStatusService } from '@core/services/rx-status.service';
import { RxStatusEnum } from '@shared/models/enums/rx-status.enum';
import { LocalSettingsQuery } from '@shared/store/localSettings/local-settings-query';
import { LocalSettingsStore } from '@shared/store/localSettings/local-settings-store';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';
import { UnifiedPatientsCommunicationService } from '@shared/services/unified-patients-communication.service';
import { PatientAppAdapter } from '@shared/services/patient-app-adapter.service';
import { combineQueries } from '@datorama/akita';
import type { Maybe } from '@/types/globals';
import { PatientAppInitService } from './services/patient-app-init.service';
import { ShellApiService } from '@shared/services/shell-api.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { emptyGuid } from '@shared/models/consts';

@Injectable()
export class PatientFacade implements IBaseFacade {
	getState$: Observable<PatientState> = this.query.patientState$;
	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('patient');
	patient$: Observable<PatientModel | null> = this.query.patient$;
	regulatoryDOBMask$: Observable<string | undefined> = this.shellQuery.regulatoryDOBMask$;
	dateFormat$: Observable<string | undefined> = this.shellQuery.dateFormat$;

	isRxValidForSave$: Observable<Maybe<boolean>> = this.shellQuery.isRxValidForSave$;
	rxStatus$: Observable<RxStatusEnum> = this.rxScanStatusServiceService.status$;
	maxPatientLastNameLength$: Observable<number | undefined> = this.shellQuery.maxPatientLastNameLength$;
	patientSectionMode$: Observable<patientSectionModeEnum> = this.query.patientSectionMode$;
	readonly unifiedPatientsEnabled$: Observable<boolean> = this.unifiedPatientsIntegrationService.isEnabled$;
	isRxStatusScannedOrUnscanned$: Observable<boolean> = this.rxStatus$.pipe(
		map(scanStatus => scanStatus === RxStatusEnum.Unscanned || scanStatus === RxStatusEnum.Scanned)
	);
	isRxStatusUnscannedAndIsNotCloned$: Observable<boolean> = combineQueries([this.rxStatus$, this.shellQuery.clonedRx$]).pipe(
		map(([scanStatus, clonedRx]) => scanStatus === RxStatusEnum.Unscanned && !clonedRx)
	);
	isClearButtonVisibleForReadOnly$: Observable<boolean> = combineQueries([
		this.isRxStatusUnscannedAndIsNotCloned$,
		this.isReadOnly$
	]).pipe(map(([isRxStatusUnscannedAndIsNotCloned, isReadOnly]) => !isReadOnly && isRxStatusUnscannedAndIsNotCloned));
	isEditButtonVisibleForReadOnly$: Observable<boolean> = combineLatest([this.isRxStatusScannedOrUnscanned$, this.isReadOnly$]).pipe(
		map(([isRxStatusScannedOrUnscanned, isReadOnly]) => !isReadOnly && isRxStatusScannedOrUnscanned)
	);

	patientPopupCommandEmitter$ = this.patientAppIframeCommunicationService.patientPopupCommandEmitter$;
	patientPopupCommandHandler$ = this.patientAppIframeCommunicationService.patientPopupCommandHandler$;
	initializePatientAppStreams$: Observable<void | MessageEvent<any>> = this.patientAppInitService.initializePatientAppStreams$;
	loadExistingUnifiedPatientHandler$: Observable<void> = this.unifiedPatientsIntegrationService.loadExistingUnifiedPatientHandler$();
	isRxFromReferralDoctorAndUserIsScannigCenter$: Observable<boolean> = this.getIsReferralFromRxAndScanningCenter();

	readonly isIntuitiveFlow$: Observable<boolean> = this.intuitiveFlowService.isIntuitiveFlow$;
	readonly patientDisplayMode$: Observable<PatientDisplayMode> = this.patientDisplayModeService.displayMode$;

	constructor(
		private store: PatientStore,
		private query: PatientQuery,
		private shellQuery: ShellQuery,
		private popupService: PopupService,
		private patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private patientAppInitService: PatientAppInitService,
		private rxScanStatusServiceService: RxStatusService,
		private localSettingQuery: LocalSettingsQuery,
		private localSettingStore: LocalSettingsStore,
		private softwareOptionsService: SoftwareOptionsService,
		private readonly intuitiveFlowService: IntuitiveFlowService,
		private readonly unifiedPatientsIntegrationService: UnifiedPatientsCommunicationService,
		private readonly patientDisplayModeService: PatientDisplayModeService,
		private readonly patientAppAdapter: PatientAppAdapter,
		private readonly shellApiService: ShellApiService,
		private readonly hostPlatformService: HostPlatformService
	) {}

	getStateSync(property?: string) {
		return property ? this.query.getValue()[property as keyof PatientState] : this.query.getValue();
	}

	clearPatient(): Observable<any> {
		const contactId = this.shellQuery.contactId;

		if (contactId != null && this.localSettingQuery.userSettings?.[contactId]?.ClearPatientWithoutConfirmation) {
			this.store.clearPatient();
			this.patientAppAdapter.clearPatient();

			return of(null);
		}

		const popUpInput = {
			titleTranslationKey: 'Popup.Confirmation',
			contentTranslationKey: 'PatientSection.ClearPatientConfirmationMessage',
			dontShowAgainAction: () => {
				if (contactId != null) {
					this.localSettingStore.updateLocalSettings(contactId, { ClearPatientWithoutConfirmation: true });
				}
			}
		};

		return this.popupService.openConfirmationPopUp({ popUpInput }).pipe(
			tap(popUpResult => {
				if (popUpResult === PopUpActions.Ok) {
					this.store.clearPatient();
					this.patientAppAdapter.clearPatient();
				}
			})
		);
	}

	getPatient(patientGuid: string): Observable<PatientModelDto | undefined> {
		if (!patientGuid || patientGuid === emptyGuid) {
			return of(undefined);
		}

		return combineLatest([this.shellQuery.doctor$, this.shellQuery.companyId$]).pipe(
			map(([doctor, companyId]) => ({
				doctorId: doctor?.Id,
				companyId
			})),
			filter((context): context is { doctorId: number; companyId: number } => context.doctorId != null && context.companyId != null),
			take(1),
			switchMap(({ doctorId, companyId }) =>
				this.shellApiService.getPatientByUid({
					patientGuid,
					doctorId,
					companyId,
					isHostPlatformScanner: this.hostPlatformService.isScanner
				})
			)
		);
	}

	private getIsReferralFromRxAndScanningCenter(): Observable<boolean> {
		return combineLatest([
			this.shellQuery.rx$,
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.ReferralWorkflowScanningCenter)
		]).pipe(
			map(([rx, isScanningCenter]) => {
				return !!(rx && rx?.Patient?.ChartNumber !== '') && isScanningCenter;
			})
		);
	}
}
